import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { getCsrfToken } from '../utils/helpers';

interface UserDateOfBirthData {
    date: string;
    updatedAt: string | null;
}
interface UserCardData {
    cardNumber: string;
    level: string;
    discount: number;
    balance: number;
}
interface FetchUsersResponse {
    users: UserData[];
    total: number;
    limit: number;
    skip: number;
}
export interface UserData {
    id: string;
    lastName: string;
    firstName: string;
    middleName: string | null;
    dateOfBirth: UserDateOfBirthData | null;
    phoneNumber: string | null;
    email: string;
    emailStatus: boolean;
    card: UserCardData | null;
    status: 'employee' | 'customer';
    createdAt: string | null;
    address: { raw?: string } | null;
    lastAuthDate: string | null;
}
interface AdminUsersState {
    users: UserData[];
    isLoading: boolean;
    error: string | null;
}

const initialState: AdminUsersState = {
    users: [],
    isLoading: false,
    error: null,
};

export const fetchUsers = createAsyncThunk<UserData[], void, { rejectValue: string }>('adminUsers/fetchUsers', async (_, { rejectWithValue }) => {
    const pageLimit = 200;
    const allUsers: UserData[] = [];
    let skip = 0;
    let total = Number.POSITIVE_INFINITY;
    try {
        while (skip < total) {
            const params = new URLSearchParams({ limit: String(pageLimit), skip: String(skip) });
            const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/users-list?${params.toString()}`, {
                method: 'GET',
                credentials: 'include',
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                return rejectWithValue(errorData?.message || 'При загрузке пользователей произошла ошибка');
            }
            const data: FetchUsersResponse = await response.json();
            const pageUsers = Array.isArray(data.users) ? data.users : [];
            allUsers.push(...pageUsers);
            total = Number.isFinite(data.total) ? data.total : allUsers.length;
            if (pageUsers.length === 0) break;
            skip += pageUsers.length;
        }
        return Array.from(new Map(allUsers.map((user) => [user.id, user])).values());
    } catch (error) {
        return rejectWithValue('Сетевая ошибка при загрузке пользователей');
    }
});

// PATCH обновление статуса пользователя // СТАТУС
export const updateUserStatus = createAsyncThunk<
    { id: string; status: 'employee' | 'customer' },
    { id: string; status: 'employee' | 'customer' },
    { rejectValue: string }
>('adminUsers/updateUserStatus', async ({ id, status }, { rejectWithValue }) => {
    try {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/admin/users/${id}/status`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify({ status }),
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Ошибка при обновлении статуса');
        }
        return { id, status };
    } catch (error) {
        return rejectWithValue('Сетевая ошибка при обновлении статуса');
    }
});

const adminUsersSlice = createSlice({
    name: 'adminUsers',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UserData[]>) => {
                state.isLoading = false;
                state.users = action.payload;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updateUserStatus.pending, (state) => {
                state.error = null;
            })
            .addCase(updateUserStatus.fulfilled, (state, action: PayloadAction<{ id: string; status: 'employee' | 'customer' }>) => {
                const { id, status } = action.payload;
                const user = state.users.find((u) => u.id === id);
                if (user) {
                    user.status = status;
                }
            })
            .addCase(updateUserStatus.rejected, (state, action) => {
                state.error = action.payload as string;
            });
    },
});

export default adminUsersSlice.reducer;

export const selectAdminUsers = (state: RootState) => state.adminUsers.users;
export const selectAdminUsersLoading = (state: RootState) => state.adminUsers.isLoading;
export const selectAdminUsersError = (state: RootState) => state.adminUsers.error;
